import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, InputAdornment, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { makeStyles } from '@mui/styles';
import { Search } from '@mui/icons-material';
import useIsMobile from 'hooks/useIsMobile';
import TagsService from 'services/TagsService';

const useStyles = makeStyles((theme) => ({
  buttonSearch: {
    cursor: 'pointer',
    marginTop: theme.spacing(0.3),
  },
}));

const Props = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default function SearchInput({ value, onChange, onSubmit, placeholder = '' }) {
  const inputElement = useRef(undefined);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTags, setSelectedTags] = useState(
    value ? value.split(' ').filter((tag) => tag !== 'illustrations') : [],
  );
  const [inputValue, setInputValue] = useState('');
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const classes = useStyles();
  const isMobile = useIsMobile();
  const debounceTimer = useRef(null);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  const handleTagAdd = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleTagDelete = (tagToDelete) => {
    setSelectedTags((tags) => tags.filter((tag) => tag !== tagToDelete));
  };

  const handleAutocompleteChange = (_, newValue) => {
    if (Array.isArray(newValue)) {
      const newTags = newValue.filter((value) => !selectedTags.includes(value));
      const updatedTags = [...selectedTags, ...newTags];
      setSelectedTags(updatedTags);
      onChange(updatedTags.join(' '));
    }
  };

  const getTags = useCallback(async (search) => {
    setSuggestions([]);
    setLoadingSuggestions(true);
    try {
      const tags = await TagsService.findAll({ search });
      setSuggestions(tags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setSuggestions([]);
    }
    setLoadingSuggestions(false);
  }, []);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      getTags(inputValue);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, getTags]);

  return (
    <>
      <Box minWidth={!isMobile ? 450 : 350} display="flex" mt={4} pb={3}>
        <Autocomplete
          fullWidth
          loading={loadingSuggestions}
          onKeyPress={handleKeyPress}
          freeSolo
          multiple
          limitTags={5}
          filterOptions={(x) => x}
          options={suggestions}
          value={selectedTags}
          onChange={handleAutocompleteChange}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          title="Select or search for tags"
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={option}
                {...getTagProps({ index })}
                color="secondary"
                size="small"
                onClick={() => handleTagAdd(option)}
                onDelete={() => handleTagDelete(option)}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              ref={inputElement}
              onKeyPress={handleKeyPress}
              variant="outlined"
              fullWidth
              label="Search or Add Tags"
              placeholder={placeholder || 'Type to search or add tags'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <Search
                      className={classes.buttonSearch}
                      onClick={onSubmit}
                      color="action"
                      fontSize="medium"
                      role="button"
                      aria-label="Search"
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Box>
    </>
  );
}

SearchInput.propTypes = Props;
